import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw } from "lucide-react";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import type { CompanyPrefilledFieldLocks } from "@/lib/cat/mdm-map-company";
import type { RetailCompany } from "@/lib/cat/retail-client.types";
import {
  getSiretLookupErrorMessage,
  isValidSiretFormat,
  SIRET_FORMAT_VALIDATION_MESSAGE,
  type SiretLookupResult,
} from "@/lib/cat/client-step-contracts";
import { CompanyCreateForm } from "@/components/backoffice/cat/vehicle-assignment/client-step/company-create-form";
import { CompanyContactEditForm } from "@/components/backoffice/cat/vehicle-assignment/client-step/company-contact-edit-form";
import { CompanyInseeRefPersPanel } from "@/components/backoffice/cat/vehicle-assignment/client-step/company-insee-refpers-panel";

function RetailCompanySection({
  companyEdit,
  companyPrefilledLocks,
  emailBrandLabel,
  isRestoringFromDraft = false,
  isVerifying = false,
  newSearchButtonLabel = "Nouvelle recherche",
  onCompanyEditChange,
  onCreateCanProceedChange,
  onResetSearch,
  onSiretChange,
  onVerify,
  siretInput,
  siretResult,
  validatedCompany,
}: {
  companyEdit: EditableCompanyProfile | null;
  companyPrefilledLocks: CompanyPrefilledFieldLocks;
  emailBrandLabel?: string | null;
  isRestoringFromDraft?: boolean;
  isVerifying?: boolean;
  newSearchButtonLabel?: string;
  onCompanyEditChange: (value: EditableCompanyProfile) => void;
  onCreateCanProceedChange?: (canProceed: boolean) => void;
  onResetSearch?: () => void;
  onSiretChange: (v: string) => void;
  onVerify: () => void;
  siretInput: string;
  siretResult: SiretLookupResult | null;
  validatedCompany: RetailCompany | null;
}) {
  const idPrefix = "retail-company";
  const siretErrorMessage = getSiretLookupErrorMessage(siretResult);
  const showInseeRefPersDetail =
    Boolean(validatedCompany) && siretResult?.kind === "known";
  const refPersMissing = Boolean(validatedCompany?.mdmReferentialMissing);
  const refPersRegistered = Boolean(
    validatedCompany && !validatedCompany.mdmReferentialMissing,
  );
  const showCompanyForm = Boolean(
    validatedCompany && companyEdit && refPersMissing,
  );
  const showContactEditForm = Boolean(
    validatedCompany && companyEdit && refPersRegistered,
  );
  const canLaunchSiretSearch = isValidSiretFormat(siretInput);
  const showSiretFormatHint = siretInput.length > 0 && !canLaunchSiretSearch;
  const searchSucceededWithResult =
    siretResult?.kind === "known" && Boolean(validatedCompany) && onResetSearch;
  const showSearchForm = !searchSucceededWithResult;

  return (
    <section className="space-y-1">
      <div
        className={cn(
          "flex flex-col gap-3",
          searchSucceededWithResult &&
            "flex-row flex-wrap items-center justify-between gap-2",
        )}
      >
        {searchSucceededWithResult ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onResetSearch}
          >
            <RotateCcw className="mr-2 size-4" aria-hidden />
            {newSearchButtonLabel}
          </Button>
        ) : null}
      </div>
      <div className="space-y-4">
        {isRestoringFromDraft ? (
          <div
            className="text-muted-foreground flex items-center gap-2 py-4 text-sm"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            Chargement des informations société…
          </div>
        ) : null}

        {showSearchForm && !isRestoringFromDraft ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <Field
              className="min-w-0 flex-1"
              data-invalid={showSiretFormatHint ? true : undefined}
            >
              <FieldLabel htmlFor={`${idPrefix}-siret`}>N° de SIRET</FieldLabel>
              <FieldContent>
                <Input
                  aria-invalid={showSiretFormatHint}
                  autoComplete="off"
                  id={`${idPrefix}-siret`}
                  inputMode="numeric"
                  maxLength={14}
                  value={siretInput}
                  onChange={(e) =>
                    onSiretChange(
                      e.target.value.replace(/\D/g, "").slice(0, 14),
                    )
                  }
                />
                {showSiretFormatHint ? (
                  <FieldError>{SIRET_FORMAT_VALIDATION_MESSAGE}</FieldError>
                ) : null}
              </FieldContent>
            </Field>
            <Button
              disabled={isVerifying || !canLaunchSiretSearch}
              type="button"
              onClick={onVerify}
            >
              {isVerifying ? "Vérification…" : "Vérifier le SIRET"}
            </Button>
          </div>
        ) : null}

        {siretErrorMessage ? (
          <Alert variant="destructive">
            <AlertTitle>Point bloquant</AlertTitle>
            <AlertDescription>{siretErrorMessage}</AlertDescription>
          </Alert>
        ) : null}

        {showInseeRefPersDetail && validatedCompany ? (
          <>
            <CompanyInseeRefPersPanel
              company={validatedCompany}
              emailBrandLabel={emailBrandLabel}
            />
            {showCompanyForm ? (
              <CompanyCreateForm
                value={companyEdit!}
                prefilledLockedFields={companyPrefilledLocks}
                onChange={onCompanyEditChange}
                onCanProceedChange={onCreateCanProceedChange}
                title="Création dans le Référentiel Personne (RefPers)"
                description="Les champs préremplis depuis l'INSEE sont en lecture seule. Complétez les contacts et les compléments d'adresse manquants si nécessaire."
              />
            ) : null}
            {showContactEditForm ? (
              <CompanyContactEditForm
                value={companyEdit!}
                onChange={onCompanyEditChange}
                onCanProceedChange={onCreateCanProceedChange}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

export { RetailCompanySection };
